// /src/pages/promotions/Functions/uploadHome.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Grid,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
  CircularProgress,
  Chip,
} from "@mui/material";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import DeleteIcon from "@mui/icons-material/Delete";
import BrokenImageIcon from "@mui/icons-material/BrokenImage";
import CropIcon from "@mui/icons-material/Crop";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import axios from "axios";
import Swal from "sweetalert2";
import { getAuthCredentials } from "../../../utils/auth";
import HandleCropPromoHome from "./HandleCropPromoHome";
import HandleTrimPromoVideo from "./HandleTrimPromoVideo";

const MAX_SIZE = 50 * 1024 * 1024;
const ALLOWED = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "video/mp4",
  "video/webm",
  "video/quicktime",
];

const safeRevoke = (url) => {
  try {
    if (url?.startsWith("blob:")) URL.revokeObjectURL(url);
  } catch {}
};

const UploadHome = ({
  open,
  onClose,
  onSuccess,
  existingItem = null,
  editIndex = null, // kept for fallback
  editId = null, // <-- use ids instead of indices
}) => {
  const isEditing =
    existingItem != null && (editId || Number.isInteger(editIndex));

  const [title, setTitle] = useState(existingItem?.title || "");
  const [alt, setAlt] = useState(existingItem?.alt || "");
  const [isActive, setIsActive] = useState(
    existingItem?.isActive !== undefined ? !!existingItem.isActive : true
  );

  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(existingItem?.url || "");
  const [type, setType] = useState(existingItem?.type || null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  // image crop
  const [cropperOpen, setCropperOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);

  // video trim
  const [trimOpen, setTrimOpen] = useState(false);
  const [trimSrc, setTrimSrc] = useState(null);

  const inputRef = useRef(null);

  const syncFromProps = () => {
    setTitle(existingItem?.title || "");
    setAlt(existingItem?.alt || "");
    setIsActive(
      existingItem?.isActive !== undefined ? !!existingItem.isActive : true
    );
    setType(existingItem?.type || null);
    setFile(null);

    safeRevoke(previewUrl);
    setPreviewUrl(existingItem?.url || "");

    setError("");

    safeRevoke(cropSrc);
    setCropSrc(null);
    setCropperOpen(false);

    // trim cleanup
    safeRevoke(trimSrc);
    setTrimSrc(null);
    setTrimOpen(false);

    if (inputRef.current) inputRef.current.value = "";
  };

  useEffect(() => {
    if (open) syncFromProps();
  }, [open, existingItem]);

  useEffect(() => {
    return () => {
      safeRevoke(previewUrl);
      safeRevoke(cropSrc);
      safeRevoke(trimSrc);
    };
  }, []);

  const handlePick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;

    if (!ALLOWED.includes(f.type)) {
      setError("Unsupported file. Use JPEG/PNG/GIF or MP4/WEBM/MOV.");
      e.target.value = "";
      return;
    }
    if (f.size > MAX_SIZE) {
      setError("File too large.");
      e.target.value = "";
      return;
    }
    setError("");

    const isGif = f.type === "image/gif";
    const isImage = f.type.startsWith("image/") && !isGif;
    const isVideo = f.type.startsWith("video/");

    if (isImage) {
      setType("image");
      safeRevoke(cropSrc);
      const obj = URL.createObjectURL(f);
      setCropSrc(obj);
      setCropperOpen(true);
    } else if (isGif) {
      setType("gif");
      setFile(f);
      safeRevoke(previewUrl);
      setPreviewUrl(URL.createObjectURL(f));
    } else if (isVideo) {
      setType("video");
      setFile(f);
      safeRevoke(previewUrl);
      setPreviewUrl(URL.createObjectURL(f));
    }
  };

  // image crop handlers
  const handleRecropExisting = () => {
    if (type !== "image" || !previewUrl) return;
    safeRevoke(cropSrc);
    setCropSrc(previewUrl);
    setCropperOpen(true);
  };
  const handleCropSave = (blob) => {
    const newFile = new File([blob], "home-image.jpg", { type: blob.type });
    setFile(newFile);
    safeRevoke(previewUrl);
    setPreviewUrl(URL.createObjectURL(blob));
    setCropperOpen(false);
    safeRevoke(cropSrc);
    setCropSrc(null);
  };
  const handleCropCancel = () => {
    setCropperOpen(false);
    safeRevoke(cropSrc);
    setCropSrc(null);
    if (!file && !isEditing && inputRef.current) inputRef.current.value = "";
  };

  // video trim handlers
  const openTrim = () => {
    // Prefer a fresh blob URL from the selected file to avoid CORS
    if (file) {
      const local = URL.createObjectURL(file);
      safeRevoke(trimSrc);
      setTrimSrc(local);
    } else {
      // Fall back to previewUrl (may be remote; dialog shows CORS error if blocked)
      safeRevoke(trimSrc);
      setTrimSrc(previewUrl || existingItem?.url || "");
    }
    setTrimOpen(true);
  };
  const closeTrim = () => {
    setTrimOpen(false);
    safeRevoke(trimSrc);
    setTrimSrc(null);
  };
  const handleTrimSave = (blob) => {
    const newFile = new File([blob], "home-video-trimmed.mp4", {
      type: "video/mp4",
    });
    setFile(newFile);
    safeRevoke(previewUrl);
    setPreviewUrl(URL.createObjectURL(blob));
    closeTrim();
  };

  const clearPicked = () => {
    setFile(null);
    setType(existingItem?.type || null);
    safeRevoke(previewUrl);
    setPreviewUrl(existingItem?.url || "");
    setError("");
    if (inputRef.current) inputRef.current.value = "";
  };

  const submit = async () => {
    try {
      setSaving(true);
      const { token } = await getAuthCredentials();

      const method = isEditing ? "put" : "post";
      const idOrIndex = editId ?? editIndex; // prefer id
      const url = isEditing
        ? `${process.env.REACT_APP_BACKEND_URL}/v1/api/promotion/home/${idOrIndex}`
        : `${process.env.REACT_APP_BACKEND_URL}/v1/api/promotion/home`;

      const form = new FormData();

      if (file) form.append("media", file);

      const resolvedType = file
        ? file.type === "image/gif"
          ? "gif"
          : file.type.startsWith("video/")
          ? "video"
          : "image"
        : type || "image";

      form.append(
        "data",
        JSON.stringify({
          type: resolvedType,
          isActive: !!isActive,
          alt: alt || "",
          title: title || "",
        })
      );

      await axios({
        url,
        method,
        headers: { Authorization: `Bearer ${token}` },
        data: form,
      });

      Swal.fire({
        icon: "success",
        title: isEditing ? "Media updated" : "Media added",
        timer: 1200,
        showConfirmButton: false,
      });

      onSuccess?.();
      onClose?.();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Save failed",
        text: err?.response?.data?.message || "Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  // IMPORTANT: close the dialog FIRST, then delete, then show SweetAlert
  const doDelete = async () => {
    if (!isEditing) return;

    onClose?.();

    const confirm = await Swal.fire({
      icon: "warning",
      title: "Delete this media?",
      text: "This action cannot be undone.",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#d32f2f",
    });
    if (!confirm.isConfirmed) return;

    try {
      const { token } = await getAuthCredentials();
      const idOrIndex = editId ?? editIndex; // prefer id
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/promotion/home/${idOrIndex}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      Swal.fire({
        icon: "success",
        title: "Deleted",
        timer: 900,
        showConfirmButton: false,
      });

      onSuccess?.();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Delete failed",
        text: err?.response?.data?.message || "Please try again.",
      });
    }
  };

  const disabledSave = useMemo(() => {
    if (!isEditing) return !file || !type || saving;
    return saving || !type;
  }, [isEditing, file, type, saving]);

  const isVideo = type === "video";

  return (
    <>
      <Dialog
        open={open}
        onClose={saving ? undefined : onClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {isEditing ? "Edit Home Media" : "Add Home Media"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<PhotoCamera />}
                  disabled={saving}
                >
                  {isEditing ? "Replace File" : "Choose File"}
                  <input
                    ref={inputRef}
                    hidden
                    accept="image/*,video/mp4,video/webm,video/quicktime"
                    type="file"
                    onChange={handlePick}
                  />
                </Button>
              </Grid>

              {type === "image" && (previewUrl || file) && (
                <Grid item>
                  <Tooltip title="Re-crop (9:16)">
                    <span>
                      <Button
                        variant="outlined"
                        startIcon={<CropIcon />}
                        onClick={handleRecropExisting}
                        disabled={saving || !previewUrl}
                      >
                        Re-crop
                      </Button>
                    </span>
                  </Tooltip>
                </Grid>
              )}

              {isVideo && (
                <Grid item>
                  <Tooltip title="Trim video (choose start & end)">
                    <span>
                      <Button
                        variant="outlined"
                        startIcon={<ContentCutIcon />}
                        onClick={openTrim}
                        disabled={saving || !(previewUrl || file)}
                      >
                        Trim Video
                      </Button>
                    </span>
                  </Tooltip>
                </Grid>
              )}

              {previewUrl ? (
                <Grid item xs>
                  {isVideo ? (
                    <video
                      src={previewUrl}
                      controls
                      muted
                      style={{
                        width: 180,
                        height: 320,
                        objectFit: "cover",
                        borderRadius: 8,
                        border: "1px solid rgba(0,0,0,0.1)",
                        background: "#000",
                      }}
                    />
                  ) : (
                    <img
                      src={previewUrl}
                      alt="Preview"
                      style={{
                        width: 180,
                        height: 320,
                        objectFit: "contain",
                        borderRadius: 8,
                        border: "1px solid rgba(0,0,0,0.1)",
                        background: "#fff",
                      }}
                    />
                  )}
                </Grid>
              ) : (
                <Grid item>
                  <BrokenImageIcon color="disabled" />
                </Grid>
              )}

              {!isEditing && (file || previewUrl) && (
                <Grid item>
                  <Tooltip title="Remove selected">
                    <span>
                      <IconButton
                        onClick={clearPicked}
                        disabled={saving}
                        color="error"
                        aria-label="Remove selected"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Grid>
              )}
            </Grid>

            {error && (
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            )}

            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary">
                Type:
              </Typography>
              <Chip
                label={type ? type.toUpperCase() : "—"}
                size="small"
                color={type ? "primary" : "default"}
                variant={type ? "filled" : "outlined"}
              />
            </Stack>

            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  disabled={saving}
                />
              }
              label="Active"
            />

            <TextField
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              disabled={saving}
            />
            <TextField
              label="Alt text"
              value={alt}
              onChange={(e) => setAlt(e.target.value)}
              fullWidth
              helperText="Shown when media cannot be displayed."
              disabled={saving}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          {isEditing && (
            <Button
              onClick={doDelete}
              color="error"
              startIcon={<DeleteIcon />}
              disabled={saving}
            >
              Delete
            </Button>
          )}
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            variant="contained"
            disabled={disabledSave}
            startIcon={saving ? <CircularProgress size={18} /> : null}
          >
            {isEditing ? "Save Changes" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image crop (9:16) */}
      <HandleCropPromoHome
        open={cropperOpen}
        imageSrc={cropSrc}
        onCancel={handleCropCancel}
        onSave={handleCropSave}
      />

      {/* Video trim */}
      <HandleTrimPromoVideo
        open={trimOpen}
        src={trimSrc || ""}
        onCancel={closeTrim}
        onSave={handleTrimSave}
      />
    </>
  );
};

export default UploadHome;
